import { FC } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { BusinessGoalAIGenerateFormData } from './business-goal-ai-generate.schema';

interface BusinessGoalAIGenerateFormProps {
  control: Control<BusinessGoalAIGenerateFormData>;
  errors: FieldErrors<BusinessGoalAIGenerateFormData>;
  isLoading: boolean;
}

const BusinessGoalAIGenerateForm: FC<BusinessGoalAIGenerateFormProps> = ({ control, errors, isLoading }) => {
  return (
    <>
      <div>
        <label htmlFor="current_revenue" className="block text-sm font-medium text-gray-700 mb-2">
          Current Revenue (Rp) <span className="text-gray-400">(Optional)</span>
        </label>
        <Controller
          name="current_revenue"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="number"
              id="current_revenue"
              min="0"
              step="1000000"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.current_revenue ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., 50000000"
              disabled={isLoading}
            />
          )}
        />
        {errors.current_revenue && <p className="text-sm text-red-500 mt-1">{errors.current_revenue.message}</p>}
        <p className="text-xs text-gray-500 mt-1">
          Contoh: 50000000 (50 juta). Tidak boleh minus. Dipakai AI sebagai baseline.
        </p>
      </div>

      <div>
        <label htmlFor="growth_target" className="block text-sm font-medium text-gray-700 mb-2">
          Growth Target (%) <span className="text-gray-400">(Optional)</span>
        </label>
        <Controller
          name="growth_target"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="number"
              id="growth_target"
              min="0"
              max="100"
              step="1"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.growth_target ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="e.g., 20"
              disabled={isLoading}
            />
          )}
        />
        {errors.growth_target && <p className="text-sm text-red-500 mt-1">{errors.growth_target.message}</p>}
        <p className="text-xs text-gray-500 mt-1">Contoh: 20 berarti pertumbuhan 20% per tahun.</p>
      </div>

      <div>
        <label htmlFor="business_type" className="block text-sm font-medium text-gray-700 mb-2">
          Business Type <span className="text-gray-400">(Optional)</span>
        </label>
        <Controller
          name="business_type"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              id="business_type"
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.business_type ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder='e.g., "B2B SaaS", "Retail", "Manufacturing"'
              disabled={isLoading}
            />
          )}
        />
        {errors.business_type && <p className="text-sm text-red-500 mt-1">{errors.business_type.message}</p>}
        <p className="text-xs text-gray-500 mt-1">Contoh: "B2B SaaS", "Retail", "Manufacturing".</p>
      </div>

      <div>
        <label htmlFor="market_size" className="block text-sm font-medium text-gray-700 mb-2">
          Market Size <span className="text-gray-400">(Optional)</span>
        </label>
        <Controller
          name="market_size"
          control={control}
          render={({ field }) => (
            <textarea
              {...field}
              id="market_size"
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.market_size ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder='e.g., "5.000.000 pengguna", "500 perusahaan menengah"'
              disabled={isLoading}
            />
          )}
        />
        {errors.market_size && <p className="text-sm text-red-500 mt-1">{errors.market_size.message}</p>}
        <p className="text-xs text-gray-500 mt-1">
          Contoh: "5.000.000 pengguna", "500 perusahaan menengah". Digunakan AI untuk memproyeksikan skala revenue.
        </p>
      </div>
    </>
  );
};

export default BusinessGoalAIGenerateForm;
